import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useBulkConfirmReplenishmentMutation,
  useBulkRejectReplenishmentMutation,
} from "../../../../store/apiSlice/warehouseApiSlice";
import { componentWithCondition } from "../../../newLayout/page/components/hoc/componentWithConditionCheck";

const isPendingReplenishment = order => {
  const statusId = order.status?.id || order.status;
  return statusId?.toLowerCase() === "pending";
};

const ReplenishmentAction = ({ selectedOrderId, setSelectedOrder, selectedOrderFromListview }) => {
  const [rejectReplenishment, { isLoading: isRejecting }] = useBulkRejectReplenishmentMutation();
  const [bulkConfirmReplenishment, { isLoading: isConfirming }] =
    useBulkConfirmReplenishmentMutation();
  const notify = useNotification();
  const navigate = useNavigate();

  const isConfirmDisabled = useMemo(() => {
    if (!selectedOrderId || selectedOrderId.length === 0) return true;

    return selectedOrderFromListview.some(order => !isPendingReplenishment(order));
  }, [selectedOrderId, selectedOrderFromListview]);

  const isRejectDisabled = useMemo(() => {
    if (!selectedOrderId || selectedOrderId.length === 0) return true;

    return selectedOrderFromListview.some(order => !isPendingReplenishment(order));
  }, [selectedOrderId, selectedOrderFromListview]);

  const handleConfirm = async () => {
    if (isConfirmDisabled || isConfirming) return;

    if (selectedOrderId.length === 1) {
      navigate(`${selectedOrderId[0]}/confirm`);
      return;
    }

    try {
      const res = await bulkConfirmReplenishment({ ids: selectedOrderId }).unwrap();
      notify({
        type: "success",
        message:
          res?.detail ||
          `${res?.confirmed ?? selectedOrderId.length} replenishment(s) confirmed successfully.`,
      });
      setSelectedOrder([]);
    } catch (error) {
      const err = error?.data;
      const message =
        err?.detail ||
        (Array.isArray(err?.ids) ? err.ids.join(", ") : err?.ids) ||
        "Something went wrong. Please try again.";
      notify({ type: "error", message });
    }
  };

  const handleBulkReject = async () => {
    if (isRejectDisabled || isRejecting) return;
    try {
      const res = await rejectReplenishment({ ids: selectedOrderId }).unwrap();
      notify({
        type: "success",
        message: res?.detail || "Selected replenishments rejected successfully.",
      });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <>
      <li className={`tableButton ${!isConfirmDisabled ? "activeState" : ""}`}>
        <button disabled={isConfirmDisabled || isConfirming} onClick={handleConfirm}>
          Confirm
        </button>
      </li>
      <li className={`tableButton ${!isRejectDisabled ? "activeState" : ""}`}>
        <button disabled={isRejectDisabled || isRejecting} onClick={handleBulkReject}>
          Reject
        </button>
      </li>
    </>
  );
};

export const ReplenishmentActionWithCheck = componentWithCondition(ReplenishmentAction);
